import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { trackWhatsAppClick, trackEventCustom } from "../../utils/analytics";

const SITE_URL = "https://hexxatech.co.in";
const PAGE_URL = `${SITE_URL}/services/mobile-app-development`;

const features = [
  {
    title: "Business Mobile Applications",
    text: "Custom mobile applications designed around your business workflows, users, operations and day-to-day requirements.",
  },
  {
    title: "Android Application Development",
    text: "Reliable Android applications for employees, customers, field teams, sales teams and business operations.",
  },
  {
    title: "Offline-First Applications",
    text: "Mobile applications that can continue working without internet connectivity and synchronize data when connectivity is restored.",
  },
  {
    title: "API & Backend Integration",
    text: "Connect mobile applications with existing ERP, CRM, databases, APIs and business management systems.",
  },
  {
    title: "Field & Employee Apps",
    text: "Digitize field activities such as inspections, service visits, data collection, approvals, attendance and reporting.",
  },
  {
    title: "Data Synchronization",
    text: "Synchronize mobile data with centralized systems while maintaining reliable workflows and operational consistency.",
  },
  {
    title: "Role-Based Access",
    text: "Control application functionality and information based on employee roles, permissions and business requirements.",
  },
  {
    title: "Reports & Notifications",
    text: "Provide users with real-time information, operational dashboards, alerts, notifications and business reports.",
  },
];

const useCases = [
  {
    title: "Field Service Applications",
    text: "Applications for technicians, service teams, evaluators and field employees to capture operational information from anywhere.",
  },
  {
    title: "Inspection Applications",
    text: "Digitize inspection forms, checklists, photographs, approvals, reports and field data collection.",
  },
  {
    title: "Sales Applications",
    text: "Help sales teams manage leads, customers, follow-ups, appointments, quotations and sales activities on mobile devices.",
  },
  {
    title: "Healthcare Applications",
    text: "Mobile solutions for healthcare teams, patient information, appointments, treatment workflows and operational activities.",
  },
  {
    title: "Inventory Applications",
    text: "Enable employees to perform stock checks, material requests, inward/outward transactions and inventory activities from mobile devices.",
  },
  {
    title: "Business Utility Apps",
    text: "Custom mobile applications for specific internal workflows, data collection, approvals, monitoring and business automation.",
  },
];

const processSteps = [
  {
    number: "01",
    title: "Requirement Analysis",
    text: "We understand your business workflow, mobile users, data requirements, devices, integrations and operational challenges.",
  },
  {
    number: "02",
    title: "Application Architecture",
    text: "We define the application structure, database interaction, APIs, authentication, synchronization and technical architecture.",
  },
  {
    number: "03",
    title: "UI/UX & Development",
    text: "We build practical mobile interfaces and develop features around the actual requirements of your business.",
  },
  {
    number: "04",
    title: "Testing & Validation",
    text: "The application is tested across workflows, devices, connectivity conditions, data synchronization and user roles.",
  },
  {
    number: "05",
    title: "Deployment & Support",
    text: "After deployment, we provide technical support, maintenance, improvements and future feature development.",
  },
];

const technologies = [
  "React Native",
  "Android",
  "REST APIs",
  "Node.js",
  "SQL Server",
  "MySQL",
  "SQLite",
  "JWT Authentication",
  "Offline Data Sync",
  "Cloud & Server Integration",
];

export default function MobileAppDevelopment() {
  useEffect(() => {
    document.title =
      "Mobile App Development Company in Pune | HexxaTech Softwares";

    const description =
      "HexxaTech Softwares develops custom mobile applications for businesses in Pune and across India, including Android apps, field service apps, inspection apps, offline applications, business apps and API-integrated mobile solutions.";

    const setMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("name", name);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    setMeta("description", description);

    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", PAGE_URL);

    const setPropertyMeta = (property, content) => {
      let element = document.querySelector(
        `meta[property="${property}"]`
      );

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("property", property);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    setPropertyMeta(
      "og:title",
      "Mobile App Development Company in Pune | HexxaTech Softwares"
    );

    setPropertyMeta("og:description", description);
    setPropertyMeta("og:type", "website");
    setPropertyMeta("og:url", PAGE_URL);
    setPropertyMeta("og:site_name", "HexxaTech Softwares");

    const existingSchema = document.getElementById(
      "hexxatech-mobile-app-schema"
    );

    if (existingSchema) {
      existingSchema.remove();
    }

    const script = document.createElement("script");

    script.id = "hexxatech-mobile-app-schema";
    script.type = "application/ld+json";

    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Mobile App Development",
      serviceType: "Mobile App Development",
      description,
      provider: {
        "@type": "Organization",
        name: "HexxaTech Softwares",
        url: SITE_URL,
      },
      areaServed: {
        "@type": "Country",
        name: "India",
      },
      url: PAGE_URL,
    });

    document.head.appendChild(script);

    return () => {
      const schema = document.getElementById(
        "hexxatech-mobile-app-schema"
      );

      if (schema) {
        schema.remove();
      }
    };
  }, []);

  const handleWhatsAppClick = () => {
    trackWhatsAppClick("mobile_app_service_page");
  };

  const serviceNavigation = [
    { title: "Custom Software Development", to: "/services/custom-software-development" },
    { title: "ERP & Business Management", to: "/services/erp-business-management" },
    { title: "CRM & Lead Management", to: "/services/crm-lead-management" },
    { title: "Healthcare Software", to: "/services/healthcare-software" },
    { title: "Inventory Management", to: "/services/inventory-management" },
    { title: "Industrial Process Software", to: "/services/industrial-process-software" },
    { title: "License Management", to: "/services/license-management" },
    { title: "Mobile App Development", to: "/services/mobile-app-development" },
  ];

  const currentIndex = serviceNavigation.findIndex(
    (service) => service.to === "/services/mobile-app-development"
  );
  const previousService =
    serviceNavigation[(currentIndex - 1 + serviceNavigation.length) % serviceNavigation.length];
  const nextService =
    serviceNavigation[(currentIndex + 1) % serviceNavigation.length];

  return (
    <div className="mobile-app-page">
      <style>{`
        * {
          box-sizing: border-box;
        }

        .mobile-app-page {
          background: #ffffff;
          color: #0f172a;
          overflow: hidden;
        }

        .mobile-app-container {
          width: min(1180px, calc(100% - 40px));
          margin: 0 auto;
        }

        /* =========================================
           HERO
        ========================================= */

        .mobile-app-hero {
          position: relative;
          overflow: hidden;
          padding: 105px 0 90px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 85% 15%,
              rgba(59,130,246,.20),
              transparent 30%
            ),
            linear-gradient(
              135deg,
              #294a7d 0%,
              #355b91 55%,
              #294a7d 100%
            );
        }

        .mobile-app-hero::before {
          content: "";
          position: absolute;
          width: 480px;
          height: 480px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,.07);
          top: -250px;
          right: -120px;
        }

        .mobile-app-hero::after {
          content: "";
          position: absolute;
          width: 260px;
          height: 260px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,.05);
          bottom: -180px;
          left: -80px;
        }

        .mobile-app-hero-grid {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1.05fr .95fr;
          gap: 70px;
          align-items: center;
        }

        .mobile-app-breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 22px;
          color: #bfdbfe;
          font-size: 12px;
        }

        .mobile-app-breadcrumb a {
          color: #dbeafe;
          text-decoration: none;
        }

        .mobile-app-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.15);
          color: #dbeafe;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          margin-bottom: 20px;
        }

        .mobile-app-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #60a5fa;
        }

        .mobile-app-hero h1 {
          margin: 0;
          max-width: 700px;
          font-size: clamp(42px, 5vw, 66px);
          line-height: 1.04;
          letter-spacing: -2.8px;
          font-weight: 800;
        }

        .mobile-app-hero h1 span {
          color: #60a5fa;
        }

        .mobile-app-hero-text {
          max-width: 650px;
          margin: 25px 0 30px;
          color: #dbeafe;
          font-size: 17px;
          line-height: 1.8;
        }

        .mobile-app-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .mobile-app-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0 20px;
          border-radius: 10px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 700;
          transition: .25s ease;
        }

        .mobile-app-btn-primary {
          background: #2563eb;
          color: #ffffff;
          box-shadow: 0 12px 25px rgba(15,23,42,.20);
        }

        .mobile-app-btn-primary:hover {
          background: #1d4ed8;
          transform: translateY(-2px);
        }

        .mobile-app-btn-light {
          color: #ffffff;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.20);
        }

        .mobile-app-btn-light:hover {
          background: rgba(255,255,255,.14);
        }

        /* =========================================
           MOBILE VISUAL
        ========================================= */

        .mobile-app-visual {
          min-height: 410px;
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
        }

        .mobile-device {
          position: relative;
          width: 245px;
          height: 455px;
          padding: 9px;
          border-radius: 34px;
          background: #0f172a;
          border: 2px solid rgba(255,255,255,.25);
          box-shadow:
            0 35px 80px rgba(15,23,42,.35),
            0 0 0 1px rgba(255,255,255,.08);
          transform: rotate(3deg);
        }

        .mobile-device-screen {
          width: 100%;
          height: 100%;
          overflow: hidden;
          border-radius: 27px;
          background: #f8fafc;
        }

        .mobile-device-notch {
          position: absolute;
          z-index: 2;
          width: 80px;
          height: 20px;
          border-radius: 0 0 14px 14px;
          background: #0f172a;
          top: 9px;
          left: 50%;
          transform: translateX(-50%);
        }

        .mobile-screen-header {
          padding: 42px 18px 20px;
          color: #ffffff;
          background:
            linear-gradient(135deg, #2563eb, #1e40af);
        }

        .mobile-screen-header small {
          display: block;
          margin-bottom: 6px;
          color: #dbeafe;
          font-size: 9px;
        }

        .mobile-screen-header strong {
          font-size: 19px;
        }

        .mobile-screen-content {
          padding: 16px;
        }

        .mobile-screen-card {
          padding: 14px;
          margin-bottom: 10px;
          border-radius: 12px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          box-shadow: 0 8px 20px rgba(15,23,42,.05);
        }

        .mobile-screen-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .mobile-screen-icon {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #eff6ff;
          color: #2563eb;
          font-weight: 800;
          font-size: 12px;
        }

        .mobile-screen-card strong {
          font-size: 12px;
        }

        .mobile-screen-line {
          height: 7px;
          border-radius: 5px;
          background: #e2e8f0;
          margin-top: 7px;
        }

        .mobile-screen-line.short {
          width: 60%;
        }

        .mobile-floating-card {
          position: absolute;
          right: 0;
          bottom: 55px;
          width: 175px;
          padding: 16px;
          border-radius: 14px;
          background: rgba(255,255,255,.95);
          color: #0f172a;
          box-shadow: 0 20px 45px rgba(15,23,42,.25);
        }

        .mobile-floating-card small {
          display: block;
          color: #64748b;
          font-size: 9px;
          margin-bottom: 5px;
        }

        .mobile-floating-card strong {
          font-size: 20px;
        }

        /* =========================================
           INTRO
        ========================================= */

        .mobile-app-section {
          padding: 90px 0;
        }

        .mobile-app-soft {
          background: #f8fafc;
        }

        .mobile-app-section-header {
          max-width: 760px;
          margin-bottom: 45px;
        }

        .mobile-app-section-header.center {
          margin-left: auto;
          margin-right: auto;
          text-align: center;
        }

        .mobile-app-label {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .mobile-app-title {
          margin: 12px 0 0;
          font-size: clamp(32px, 4vw, 48px);
          line-height: 1.1;
          letter-spacing: -1.8px;
        }

        .mobile-app-description {
          margin: 18px 0 0;
          color: #64748b;
          font-size: 16px;
          line-height: 1.8;
        }

        .mobile-app-intro-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 70px;
          align-items: center;
        }

        .mobile-app-intro-text p {
          color: #64748b;
          font-size: 15px;
          line-height: 1.85;
          margin: 0 0 18px;
        }

        .mobile-app-highlight {
          padding: 32px;
          border-radius: 22px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          box-shadow: 0 20px 50px rgba(15,23,42,.06);
        }

        .mobile-app-highlight h3 {
          margin: 0 0 22px;
          font-size: 21px;
        }

        .mobile-app-highlight-list {
          display: grid;
          gap: 11px;
        }

        .mobile-app-highlight-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border-radius: 10px;
          background: #f8fafc;
          color: #475569;
          font-size: 13px;
        }

        .mobile-app-check {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: #eff6ff;
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
        }

        /* =========================================
           FEATURES
        ========================================= */

        .mobile-app-feature-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .mobile-app-feature {
          padding: 25px;
          min-height: 220px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          transition: .3s ease;
        }

        .mobile-app-feature:hover {
          transform: translateY(-5px);
          border-color: #bfdbfe;
          box-shadow: 0 18px 40px rgba(15,23,42,.07);
        }

        .mobile-app-feature-number {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .mobile-app-feature h3 {
          margin: 17px 0 9px;
          font-size: 16px;
        }

        .mobile-app-feature p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        /* =========================================
           USE CASES
        ========================================= */

        .mobile-app-use-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
        }

        .mobile-app-use-card {
          padding: 28px;
          border-radius: 17px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          transition: .3s ease;
        }

        .mobile-app-use-card:hover {
          transform: translateY(-4px);
          border-color: #bfdbfe;
          box-shadow: 0 18px 40px rgba(15,23,42,.07);
        }

        .mobile-app-use-icon {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          border-radius: 11px;
          background: #eff6ff;
          color: #2563eb;
          font-weight: 800;
        }

        .mobile-app-use-card h3 {
          margin: 0 0 9px;
          font-size: 17px;
        }

        .mobile-app-use-card p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.7;
        }

        /* =========================================
           PROCESS
        ========================================= */

        .mobile-app-process-grid {
          display: grid;
          grid-template-columns: .9fr 1.1fr;
          gap: 70px;
          align-items: start;
        }

        .mobile-app-process-list {
          display: grid;
          gap: 10px;
          margin-top: 35px;
        }

        .mobile-app-process {
          padding: 20px;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }

        .mobile-app-process-number {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .mobile-app-process h3 {
          margin: 7px 0;
          font-size: 15px;
        }

        .mobile-app-process p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        .mobile-app-process-visual {
          padding: 35px;
          border-radius: 24px;
          background:
            linear-gradient(
              145deg,
              #eff6ff,
              #ffffff
            );
          border: 1px solid #dbeafe;
        }

        .mobile-app-flow {
          display: grid;
          gap: 10px;
        }

        .mobile-app-flow-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          border-radius: 12px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }

        .mobile-app-flow-icon {
          width: 32px;
          height: 32px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 10px;
          font-weight: 800;
        }

        .mobile-app-flow-row strong {
          font-size: 12px;
        }

        /* =========================================
           TECHNOLOGY
        ========================================= */

        .mobile-app-tech {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 30px;
        }

        .mobile-app-tech-item {
          padding: 11px 14px;
          border-radius: 10px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #475569;
          font-size: 12px;
          font-weight: 600;
        }

        /* =========================================
           CTA
        ========================================= */

        .mobile-app-cta {
          padding: 85px 0;
        }

        .mobile-app-cta-box {
          position: relative;
          overflow: hidden;
          padding: 70px 30px;
          text-align: center;
          border-radius: 24px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 85% 15%,
              rgba(59,130,246,.30),
              transparent 30%
            ),
            linear-gradient(
              135deg,
              #0f172a,
              #172554
            );
        }

        .mobile-app-cta-label {
          display: inline-block;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.10);
          color: #bfdbfe;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .mobile-app-cta h2 {
          max-width: 720px;
          margin: 20px auto 12px;
          font-size: clamp(34px, 4vw, 50px);
          line-height: 1.1;
          letter-spacing: -1.5px;
        }

        .mobile-app-cta p {
          max-width: 650px;
          margin: 0 auto 28px;
          color: #cbd5e1;
          font-size: 15px;
          line-height: 1.8;
        }

        .mobile-app-cta .mobile-app-btn-primary {
          background: #ffffff;
          color: #0f172a;
        }

        .mobile-app-cta .mobile-app-btn-primary:hover {
          background: #eff6ff;
        }

        /* =========================================
           SEO CONTENT
        ========================================= */

        .mobile-app-seo {
          padding: 0 0 75px;
        }

        .mobile-app-seo-box {
          max-width: 900px;
          margin: 0 auto;
        }

        .mobile-app-seo-box h2 {
          margin: 0 0 14px;
          font-size: 24px;
        }

        .mobile-app-seo-box h3 {
          margin: 28px 0 10px;
          font-size: 19px;
        }

        .mobile-app-seo-box p {
          margin: 0 0 15px;
          color: #64748b;
          font-size: 14px;
          line-height: 1.8;
        }

        /* =========================================
           RESPONSIVE
        ========================================= */

        @media (max-width: 1000px) {
          .mobile-app-hero-grid {
            grid-template-columns: 1fr;
            gap: 50px;
          }

          .mobile-app-feature-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .mobile-app-intro-grid,
          .mobile-app-process-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }

          .mobile-app-use-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 700px) {
          .mobile-app-container {
            width: min(100% - 28px, 1180px);
          }

          .mobile-app-hero {
            padding: 75px 0 65px;
          }

          .mobile-app-hero h1 {
            font-size: 42px;
            letter-spacing: -1.8px;
          }

          .mobile-app-hero-text {
            font-size: 15px;
          }

          .mobile-app-visual {
            min-height: 430px;
          }

          .mobile-device {
            width: 220px;
            height: 415px;
          }

          .mobile-floating-card {
            right: 0;
            bottom: 30px;
            width: 155px;
          }

          .mobile-app-section {
            padding: 65px 0;
          }

          .mobile-app-feature-grid,
          .mobile-app-use-grid {
            grid-template-columns: 1fr;
          }

          .mobile-app-process-visual {
            padding: 20px;
          }

          .mobile-app-cta {
            padding: 65px 0;
          }

          .mobile-app-cta-box {
            padding: 50px 20px;
            border-radius: 18px;
          }
        }
      `}</style>

      {/* =========================================
          HERO
      ========================================= */}

      <section className="mobile-app-hero">
        <div className="mobile-app-container mobile-app-hero-grid">
          <div>
            <div className="mobile-app-breadcrumb">
              <Link to="/">Home</Link>
              <span>›</span>
              <Link to="/services">Services</Link>
              <span>›</span>
              <span>Mobile App Development</span>
            </div>

            <div className="mobile-app-eyebrow">
              <span className="mobile-app-eyebrow-dot"></span>
              Mobile App Development
            </div>

            <h1>
              Mobile applications built around
              <br />
              <span>your business workflow.</span>
            </h1>

            <p className="mobile-app-hero-text">
              HexxaTech Softwares develops custom mobile applications for
              businesses that need practical, reliable and workflow-driven
              mobile solutions. From field applications and inspection apps
              to offline systems and API-connected business applications, we
              build mobile software around your requirements.
            </p>

            <div className="mobile-app-buttons">
              <a
                href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20a%20mobile%20app%20development%20project."
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-app-btn mobile-app-btn-primary"
                onClick={handleWhatsAppClick}
              >
                Discuss Your App →
              </a>

              <Link
                to="/case-studies"
                className="mobile-app-btn mobile-app-btn-light"
                onClick={() =>
                  trackEventCustom("mobile_app_case_studies_click", {
                    location: "mobile_app_hero",
                  })
                }
              >
                View Our Work →
              </Link>
            </div>
          </div>

          <div className="mobile-app-visual">
            <div className="mobile-device">
              <div className="mobile-device-notch"></div>

              <div className="mobile-device-screen">
                <div className="mobile-screen-header">
                  <small>BUSINESS APPLICATION</small>
                  <strong>Operations Dashboard</strong>
                </div>

                <div className="mobile-screen-content">
                  <div className="mobile-screen-card">
                    <div className="mobile-screen-card-top">
                      <div className="mobile-screen-icon">
                        CRM
                      </div>

                      <strong>24 Leads</strong>
                    </div>

                    <div className="mobile-screen-line"></div>
                    <div className="mobile-screen-line short"></div>
                  </div>

                  <div className="mobile-screen-card">
                    <div className="mobile-screen-card-top">
                      <div className="mobile-screen-icon">
                        INS
                      </div>

                      <strong>18 Inspections</strong>
                    </div>

                    <div className="mobile-screen-line"></div>
                    <div className="mobile-screen-line short"></div>
                  </div>

                  <div className="mobile-screen-card">
                    <div className="mobile-screen-card-top">
                      <div className="mobile-screen-icon">
                        SYNC
                      </div>

                      <strong>Data Synced</strong>
                    </div>

                    <div className="mobile-screen-line"></div>
                    <div className="mobile-screen-line short"></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mobile-floating-card">
              <small>OFFLINE CAPABILITY</small>
              <strong>Sync when online</strong>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          INTRO
      ========================================= */}

      <section className="mobile-app-section">
        <div className="mobile-app-container mobile-app-intro-grid">
          <div className="mobile-app-intro-text">
            <span className="mobile-app-label">
              Custom Mobile Applications
            </span>

            <h2 className="mobile-app-title">
              Turn your business workflow into a mobile experience.
            </h2>

            <p style={{ marginTop: "22px" }}>
              Mobile applications can help employees, field teams, sales
              teams, customers and operational staff access the information
              they need without being tied to a desktop system.
            </p>

            <p>
              At HexxaTech Softwares, we develop business-focused mobile
              applications based on the way your organization actually works.
              Instead of creating generic applications, we understand your
              workflow and build the mobile experience around it.
            </p>

            <p>
              Our mobile applications can integrate with existing ERP, CRM,
              databases and APIs while supporting authentication, role-based
              access, offline operation, data synchronization and reporting.
            </p>
          </div>

          <div className="mobile-app-highlight">
            <h3>What your mobile app can include</h3>

            <div className="mobile-app-highlight-list">
              {[
                "Custom business workflows",
                "Offline data collection",
                "API integration",
                "Real-time synchronization",
                "Role-based access",
                "Photos and document capture",
                "Push notifications",
                "Reports and dashboards",
              ].map((item) => (
                <div
                  className="mobile-app-highlight-item"
                  key={item}
                >
                  <span className="mobile-app-check">✓</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          FEATURES
      ========================================= */}

      <section className="mobile-app-section mobile-app-soft">
        <div className="mobile-app-container">
          <div className="mobile-app-section-header center">
            <span className="mobile-app-label">
              Mobile Development Services
            </span>

            <h2 className="mobile-app-title">
              Mobile software designed for real business operations.
            </h2>

            <p className="mobile-app-description">
              We develop mobile applications that connect people, processes
              and business information in one practical system.
            </p>
          </div>

          <div className="mobile-app-feature-grid">
            {features.map((feature, index) => (
              <article
                className="mobile-app-feature"
                key={feature.title}
                onClick={() =>
                  trackEventCustom("mobile_app_feature_interest", {
                    feature: feature.title,
                  })
                }
              >
                <div className="mobile-app-feature-number">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <h3>{feature.title}</h3>

                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          USE CASES
      ========================================= */}

      <section className="mobile-app-section">
        <div className="mobile-app-container">
          <div className="mobile-app-section-header">
            <span className="mobile-app-label">
              Business Use Cases
            </span>

            <h2 className="mobile-app-title">
              Mobile applications for different business workflows.
            </h2>

            <p className="mobile-app-description">
              From field operations to healthcare and sales, custom mobile
              software can be designed around specific operational needs.
            </p>
          </div>

          <div className="mobile-app-use-grid">
            {useCases.map((item, index) => (
              <article
                className="mobile-app-use-card"
                key={item.title}
              >
                <div className="mobile-app-use-icon">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <h3>{item.title}</h3>

                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          DEVELOPMENT PROCESS
      ========================================= */}

      <section className="mobile-app-section mobile-app-soft">
        <div className="mobile-app-container mobile-app-process-grid">
          <div>
            <span className="mobile-app-label">
              Development Process
            </span>

            <h2 className="mobile-app-title">
              From business requirement to working mobile application.
            </h2>

            <p className="mobile-app-description">
              Our development process focuses on understanding the business
              first and then building a mobile solution that is practical,
              maintainable and scalable.
            </p>

            <div className="mobile-app-process-list">
              {processSteps.map((step) => (
                <div
                  className="mobile-app-process"
                  key={step.number}
                >
                  <div className="mobile-app-process-number">
                    {step.number}
                  </div>

                  <h3>{step.title}</h3>

                  <p>{step.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mobile-app-process-visual">
            <span className="mobile-app-label">
              Application Workflow
            </span>

            <h3
              style={{
                margin: "12px 0 25px",
                fontSize: "23px",
              }}
            >
              Connect your users with the information they need.
            </h3>

            <div className="mobile-app-flow">
              {[
                "Mobile User",
                "Mobile Application",
                "API & Authentication",
                "Business Logic",
                "Database & Central System",
              ].map((item, index) => (
                <div
                  className="mobile-app-flow-row"
                  key={item}
                >
                  <div className="mobile-app-flow-icon">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <strong>{item}</strong>
                </div>
              ))}
            </div>

            <p
              style={{
                margin: "22px 0 0",
                color: "#64748b",
                fontSize: "13px",
                lineHeight: 1.7,
              }}
            >
              Your mobile application can work as a standalone business
              system or connect with your existing ERP, CRM, database or
              backend APIs.
            </p>
          </div>
        </div>
      </section>

      {/* =========================================
          TECHNOLOGIES
      ========================================= */}

      <section className="mobile-app-section">
        <div className="mobile-app-container">
          <span className="mobile-app-label">
            Technology & Capabilities
          </span>

          <h2 className="mobile-app-title">
            Technology chosen around your application requirements.
          </h2>

          <p className="mobile-app-description">
            We use modern application technologies and backend architectures
            to build secure, scalable and maintainable mobile business
            applications.
          </p>

          <div className="mobile-app-tech">
            {technologies.map((technology) => (
              <span
                className="mobile-app-tech-item"
                key={technology}
              >
                {technology}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          SERVICE NAVIGATION
      ========================================= */}

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <span className="mobile-app-label">
            Explore Our Services
          </span>

          <h2 className="mobile-app-title" style={{ marginTop: "12px" }}>
            Explore our other software development services.
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
              marginTop: "22px",
            }}
          >
            {serviceNavigation.map((service) => {
              const isCurrent = service.to === "/services/mobile-app-development";

              return (
                <Link
                  key={service.to}
                  to={service.to}
                  style={{
                    display: "block",
                    padding: "16px 18px",
                    borderRadius: "12px",
                    textDecoration: "none",
                    border: isCurrent ? "1px solid #2563eb" : "1px solid #e5e7eb",
                    background: isCurrent ? "#eff6ff" : "#f8fafc",
                    color: isCurrent ? "#1d4ed8" : "#334155",
                    fontWeight: isCurrent ? 700 : 600,
                  }}
                >
                  {service.title}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              borderTop: "1px solid #e5e7eb",
              paddingTop: "20px",
            }}
          >
            <Link
              to={previousService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              ← Previous: {previousService.title}
            </Link>

            <Link
              to={nextService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              Back to Custom Software Development →
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================
          CTA
      ========================================= */}

      <section className="mobile-app-cta">
        <div className="mobile-app-container">
          <div className="mobile-app-cta-box">
            <span className="mobile-app-cta-label">
              Start Your Mobile App
            </span>

            <h2>
              Have a business workflow that needs to go mobile?
            </h2>

            <p>
              Tell us about your users, workflow and requirements. We can help
              you plan and develop a custom mobile application that works
              around your business.
            </p>

            <a
              href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20a%20mobile%20app%20development%20project."
              target="_blank"
              rel="noopener noreferrer"
              className="mobile-app-btn mobile-app-btn-primary"
              onClick={() =>
                trackWhatsAppClick("mobile_app_bottom_cta")
              }
            >
              Discuss Your Mobile App →
            </a>
          </div>
        </div>
      </section>

      {/* =========================================
          SEO CONTENT
      ========================================= */}

      <section className="mobile-app-seo">
        <div className="mobile-app-container">
          <div className="mobile-app-seo-box">
            <h2>
              Mobile App Development Company in Pune
            </h2>

            <p>
              HexxaTech Softwares is a mobile app development company in Pune
              providing custom mobile application development services for
              businesses across India. We build Android and business-focused
              mobile applications around specific workflows, operational
              requirements and user needs.
            </p>

            <p>
              Our mobile application development services include business
              applications, field service applications, inspection apps,
              employee applications, sales applications, healthcare apps,
              inventory applications, offline mobile applications and
              API-integrated mobile solutions.
            </p>

            <h3>
              Custom Mobile Application Development
            </h3>

            <p>
              Every business has different processes. Instead of forcing your
              organization to adapt to a generic application, we develop
              mobile software around your actual workflow. Applications can
              include authentication, role-based access, data collection,
              photographs, documents, notifications, reports and integration
              with existing business systems.
            </p>

            <h3>
              Offline Mobile Applications
            </h3>

            <p>
              For field teams and businesses operating in locations with
              unreliable internet connectivity, offline functionality can be
              an important requirement. Our applications can store required
              information locally and synchronize data with the central system
              when connectivity becomes available.
            </p>

            <h3>
              Mobile App Development for Businesses
            </h3>

            <p>
              Whether you need a field inspection application, employee
              application, sales app, healthcare application, inventory
              application or a custom business utility app, HexxaTech
              Softwares can design and develop the solution around your
              operational requirements.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}